import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LockIcon, CheckCircleIcon, BookOpenIcon, UserCheckIcon, RocketIcon, ListIcon, PlusCircleIcon, HomeIcon, UsersIcon, EyeIcon, FileTextIcon, TrendingUpIcon, ZapIcon, TargetIcon, DollarSignIcon, ClockIcon, SparklesIcon, ArrowRightIcon, StarIcon, TrophyIcon, BriefcaseIcon, PlayCircleIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useProgress } from '../hooks/useProgress';
export function RentalModuleLocked() {
  const {
    user,
    unlockedAll
  } = useAuth();
  const {
    getCourseProgress
  } = useProgress();
  const [hoveredFeature, setHoveredFeature] = useState<number | null>(null);
  // Check progress
  const course1Progress = getCourseProgress('concept-clc', 6);
  const hasCompletedCourse1 = course1Progress === 100;
  const hasCompletedOnboarding = user ? !!(user.fullName && user.phone && user.dateOfBirth && user.city && user.address) : false;
  const course2Progress = getCourseProgress('proceduri-inchiriere', 18);
  const hasCompletedCourse2 = course2Progress === 100;
  // Calculate overall progress
  const steps = [{
    id: 1,
    completed: hasCompletedCourse1,
    label: 'Step 1 - Training 1',
    progress: course1Progress
  }, {
    id: 2,
    completed: hasCompletedOnboarding,
    label: 'Step 2 - Onboarding',
    progress: hasCompletedOnboarding ? 100 : 0
  }, {
    id: 3,
    completed: hasCompletedCourse2,
    label: 'Step 3 - Training 2',
    progress: course2Progress
  }];
  const completedSteps = steps.filter(s => s.completed).length;
  // Calculate progress: each step is worth 33.33% of total
  const step1Contribution = course1Progress / 100 * 33.33;
  const step2Contribution = hasCompletedOnboarding ? 33.33 : 0;
  const step3Contribution = course2Progress / 100 * 33.33;
  const progressPercentage = Math.round(step1Contribution + step2Contribution + step3Contribution);
  // Determine next action
  const getNextAction = () => {
    if (!hasCompletedCourse1) {
      return {
        text: 'Începe Training 1 Acum',
        path: '/cursuri/concept-clc',
        icon: PlayCircleIcon
      };
    }
    if (!hasCompletedOnboarding) {
      return {
        text: 'Completează Profilul',
        path: '/onboarding',
        icon: UserCheckIcon
      };
    }
    if (!hasCompletedCourse2) {
      return {
        text: 'Începe Training 2 Acum',
        path: '/cursuri/proceduri-inchiriere',
        icon: PlayCircleIcon
      };
    }
    return {
      text: 'Acces Deblocat!',
      path: '/inchiriere',
      icon: RocketIcon
    };
  };
  const nextAction = getNextAction();
  const lockedFeatures = [{
    title: 'Liste de prospectare',
    description: 'Proprietari verificați zilnic, gata de contactat',
    icon: ListIcon,
    gradient: 'from-blue-500 via-blue-600 to-indigo-600',
    stats: '10-20 leaduri/zi',
    highlight: 'Cel mai rapid start'
  }, {
    title: 'Creează anunț',
    description: 'Anunțuri profesionale generate automat cu AI',
    icon: PlusCircleIcon,
    gradient: 'from-emerald-500 via-green-600 to-teal-600',
    stats: '2 min/anunț',
    highlight: 'Economisești 90% timp'
  }, {
    title: 'Portofoliu proprietăți',
    description: 'Dashboard complet pentru gestionare',
    icon: HomeIcon,
    gradient: 'from-orange-500 via-red-500 to-pink-600',
    stats: 'Nelimitat',
    highlight: 'Organizare perfectă'
  }, {
    title: 'Leaduri în timp real',
    description: 'Cereri de închiriere direct din piață',
    icon: UsersIcon,
    gradient: 'from-purple-500 via-violet-600 to-purple-700',
    stats: '24/7 activ',
    highlight: 'Niciodată fără clienți'
  }, {
    title: 'Programare Vizionări',
    description: 'Programare automată și reminder-uri',
    icon: EyeIcon,
    gradient: 'from-cyan-500 via-blue-600 to-indigo-600',
    stats: 'Auto-pilot',
    highlight: 'Zero pierderi'
  }, {
    title: 'Contracte digitale',
    description: 'Generare și semnare electronică instant',
    icon: FileTextIcon,
    gradient: 'from-teal-500 via-emerald-600 to-green-600',
    stats: '2 min/contract',
    highlight: 'Legal & rapid'
  }];
  const benefits = [{
    icon: ZapIcon,
    title: 'Viteză 10X',
    description: 'Automatizare completă',
    color: 'from-yellow-400 to-orange-500',
    stat: '90% mai rapid'
  }, {
    icon: DollarSignIcon,
    title: 'Câștiguri reale',
    description: 'Din prima lună',
    color: 'from-emerald-400 to-green-500',
    stat: 'Tranzacții în prima lună'
  }, {
    icon: TargetIcon,
    title: 'Claritate totală',
    description: 'Știi exact ce faci',
    color: 'from-blue-400 to-indigo-500',
    stat: '100% ghidat'
  }, {
    icon: TrophyIcon,
    title: 'Suport premium',
    description: 'Echipă dedicată',
    color: 'from-purple-400 to-pink-500',
    stat: '24/7 disponibil'
  }];
  return <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50" data-id="element-11432">
      {/* Hero Section - Mobile Optimized */}
      <section className="relative bg-gradient-to-br from-[#132C53] to-[#1e4278] text-white pt-20 pb-12 sm:pt-24 sm:pb-16 md:pt-32 md:pb-24 overflow-hidden" data-id="element-11433">
        {/* Background pattern overlay */}
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-11434"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-11435">
          <div className="text-center max-w-4xl mx-auto" data-id="element-11436">
            {/* Lock Badge */}
            <motion.div initial={{
            opacity: 0,
            scale: 0
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.2,
            type: 'spring',
            stiffness: 200
          }} className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full mb-4 sm:mb-6 border border-white/20" data-id="element-11437">
              <LockIcon className="w-4 h-4 text-yellow-400" data-id="element-11438" />
              <span className="text-xs sm:text-sm font-medium text-white" data-id="element-11439">
                Modul temporar blocat
              </span>
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-extrabold tracking-tight mb-4 sm:mb-6 text-white leading-tight px-2 sm:px-0" data-id="element-11440">
              <span className="block mb-2" data-id="element-11441">Modul Închiriere</span>
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#357BEB] to-[#21A366]" data-id="element-11442">
                Așteaptă să fie deblocat
              </span>
            </motion.h1>

            <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.4
          }} className="text-base sm:text-lg md:text-2xl text-gray-300 mb-6 sm:mb-10 max-w-2xl mx-auto leading-relaxed px-4 sm:px-0" data-id="element-11443">
              Cel mai avansat sistem de închiriere din România. Finalizează
              trainingul și începe să câștigi bani real.
            </motion.p>

            {/* Progress indicator - Mobile Optimized */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.6
          }} className="inline-flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4 bg-white/10 backdrop-blur-xl rounded-2xl px-4 sm:px-8 py-4 border border-white/20 w-full sm:w-auto max-w-md sm:max-w-none mx-auto" data-id="element-11444">
              <div className="flex items-center space-x-2" data-id="element-11445">
                <div className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse" data-id="element-11446" />
                <span className="text-white font-semibold text-sm sm:text-base" data-id="element-11447">
                  Status:
                </span>
              </div>
              <div className="px-3 sm:px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-xl w-full sm:w-auto text-center" data-id="element-11448">
                <span className="font-bold text-slate-900 text-sm sm:text-base" data-id="element-11449">
                  Trainee - Lipsă On-boarding
                </span>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Decorative blur elements */}
        <div className="absolute -top-10 -right-10 w-40 h-40 sm:w-64 sm:h-64 bg-[#357BEB] rounded-full blur-3xl opacity-20" data-id="element-11450"></div>
        <div className="absolute -bottom-10 -left-10 w-40 h-40 sm:w-64 sm:h-64 bg-[#21A366] rounded-full blur-3xl opacity-20" data-id="element-11451"></div>
      </section>

      {/* Progress Steps Section - Mobile Optimized */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 sm:-mt-16 mb-12 sm:mb-20" data-id="element-11452">
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.8
      }} className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 shadow-2xl border border-gray-200" data-id="element-11453">
          <div className="text-center mb-8 sm:mb-12" data-id="element-11454">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#132C53] mb-3 sm:mb-4" data-id="element-11455">
              3 Pași Simpli Până la Succes
            </h2>
            <p className="text-gray-600 text-sm sm:text-base md:text-lg" data-id="element-11456">
              Deblocarea este automată după finalizare
            </p>
          </div>

          {/* Progress bar */}
          <div className="mb-8 sm:mb-12" data-id="element-11457">
            <div className="flex justify-between items-center mb-3 sm:mb-4" data-id="element-11458">
              <span className="text-[#132C53] font-semibold text-sm sm:text-base" data-id="element-11459">
                Progres total
              </span>
              <span className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent" data-id="element-11460">
                {progressPercentage}%
              </span>
            </div>
            <div className="relative h-3 sm:h-4 bg-gray-200 rounded-full overflow-hidden" data-id="element-11461">
              <motion.div initial={{
              width: 0
            }} animate={{
              width: `${progressPercentage}%`
            }} transition={{
              duration: 1.5,
              delay: 1,
              ease: 'easeOut'
            }} className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-full" data-id="element-11462" />
            </div>
          </div>

          {/* Steps grid - Mobile: 1 col, Tablet: 2 cols, Desktop: 3 cols */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-id="element-11463">
            {/* Step 1 - Active by default */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 1
          }} className="relative group" data-id="element-11464">
              <div className="relative rounded-xl sm:rounded-2xl p-5 sm:p-6 transition-all duration-300 bg-gradient-to-br from-[#357BEB] to-[#2868d8] text-white ring-4 ring-[#357BEB] ring-opacity-50 min-h-[240px] sm:min-h-[280px] flex flex-col" data-id="element-11465">
                {/* Step number */}
                <div className="flex items-center justify-between mb-4" data-id="element-11466">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center bg-white/20 backdrop-blur-sm" data-id="element-11467">
                    <span className="text-xl sm:text-2xl font-bold text-white" data-id="element-11468">
                      1
                    </span>
                  </div>
                </div>

                <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 text-white" data-id="element-11469">
                  Step 1 - Training 1
                </h3>

                {/* Status message */}
                <div className="mb-4" data-id="element-11470">
                  <div className="px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold bg-white/20" data-id="element-11471">
                    <span className="text-white" data-id="element-11472">În progres</span>
                  </div>
                </div>

                {/* CTA */}
                <Link to="/cursuri/concept-clc" className="inline-flex items-center space-x-2 text-white font-semibold hover:translate-x-1 transition-transform mt-auto text-sm sm:text-base min-h-[44px] justify-start" data-id="element-11473">
                  <span data-id="element-11474">Începe acum</span>
                  <ArrowRightIcon className="w-4 h-4" data-id="element-11475" />
                </Link>
              </div>
            </motion.div>

            {/* Step 2 - Locked */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 1.2
          }} className="relative group" data-id="element-11476">
              <div className="relative rounded-xl sm:rounded-2xl p-5 sm:p-6 transition-all duration-300 bg-white border-2 border-gray-300 text-gray-900 min-h-[240px] sm:min-h-[280px] flex flex-col" data-id="element-11477">
                {/* Step number */}
                <div className="flex items-center justify-between mb-4" data-id="element-11478">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center bg-gray-100" data-id="element-11479">
                    <span className="text-xl sm:text-2xl font-bold text-gray-400" data-id="element-11480">
                      2
                    </span>
                  </div>
                </div>

                <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 text-gray-900" data-id="element-11481">
                  Step 2 - Finalizează On-boarding
                </h3>

                {/* Status message */}
                <div className="mb-4" data-id="element-11482">
                  <div className="px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold bg-gray-100" data-id="element-11483">
                    <span className="text-gray-500" data-id="element-11484">Finalizează Curs 1</span>
                  </div>
                </div>

                {/* Spacer to match Step 1 height */}
                <div className="mt-auto h-[28px] sm:h-[28px]" data-id="element-11485"></div>
              </div>
            </motion.div>

            {/* Step 3 - Locked */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 1.4
          }} className="relative group sm:col-span-2 lg:col-span-1" data-id="element-11486">
              <div className="relative rounded-xl sm:rounded-2xl p-5 sm:p-6 transition-all duration-300 bg-white border-2 border-gray-300 text-gray-900 min-h-[240px] sm:min-h-[280px] flex flex-col" data-id="element-11487">
                {/* Step number */}
                <div className="flex items-center justify-between mb-4" data-id="element-11488">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center bg-gray-100" data-id="element-11489">
                    <span className="text-xl sm:text-2xl font-bold text-gray-400" data-id="element-11490">
                      3
                    </span>
                  </div>
                </div>

                <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 text-gray-900" data-id="element-11491">
                  Step 3 - Training 2
                </h3>

                {/* Status message */}
                <div className="mb-4" data-id="element-11492">
                  <div className="px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold bg-gray-100" data-id="element-11493">
                    <span className="text-gray-500" data-id="element-11494">
                      Finalizează Training 1 și On-boarding
                    </span>
                  </div>
                </div>

                {/* Spacer to match Step 1 height */}
                <div className="mt-auto h-[28px] sm:h-[28px]" data-id="element-11495"></div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Features Grid Section - Mobile Optimized */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12 sm:mb-20" data-id="element-11496">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.2
      }} className="text-center mb-8 sm:mb-12" data-id="element-11497">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#132C53] mb-3 sm:mb-4 px-4" data-id="element-11498">
            Ce Deblochezi După Training
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 px-4" data-id="element-11499">
            Funcționalități premium care te fac să câștigi mai mult
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-id="element-11500">
          {lockedFeatures.map((feature, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 1.4 + index * 0.1
        }} onHoverStart={() => setHoveredFeature(index)} onHoverEnd={() => setHoveredFeature(null)} className="group relative" data-id="element-11501">
              <div className="relative h-full bg-white rounded-xl sm:rounded-2xl p-5 sm:p-6 border-2 border-gray-200 transition-all duration-300 hover:border-blue-300 hover:shadow-xl" data-id="element-11502">
                {/* Lock overlay */}
                <div className="absolute top-3 right-3 sm:top-4 sm:right-4 w-9 h-9 sm:w-10 sm:h-10 bg-slate-900/80 backdrop-blur-sm rounded-full flex items-center justify-center" data-id="element-11503">
                  <LockIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" data-id="element-11504" />
                </div>

                {/* Icon */}
                <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`} data-id="element-11505">
                  <feature.icon className="w-7 h-7 sm:w-8 sm:h-8 text-white" data-id="element-11506" />
                </div>

                {/* Content */}
                <h3 className="text-lg sm:text-xl font-bold text-[#132C53] mb-2" data-id="element-11507">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-sm mb-4" data-id="element-11508">
                  {feature.description}
                </p>

                {/* Stats - Mobile: Stack vertically, Desktop: Side by side */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-between gap-2" data-id="element-11509">
                  <div className="px-3 py-1.5 bg-gray-100 rounded-lg" data-id="element-11510">
                    <span className="text-xs font-bold text-gray-700" data-id="element-11511">
                      {feature.stats}
                    </span>
                  </div>
                  <div className="px-3 py-1.5 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-lg" data-id="element-11512">
                    <span className="text-xs font-bold text-slate-900" data-id="element-11513">
                      {feature.highlight}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>

      {/* Benefits Section - Mobile Optimized */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12 sm:mb-20" data-id="element-11514">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6" data-id="element-11515">
          {benefits.map((benefit, index) => <motion.div key={index} initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 1.8 + index * 0.1
        }} whileHover={{
          scale: 1.05,
          y: -5
        }} className="relative group" data-id="element-11516">
              <div className="bg-white rounded-xl sm:rounded-2xl p-5 sm:p-6 border-2 border-gray-200 text-center hover:border-blue-300 hover:shadow-xl transition-all" data-id="element-11517">
                <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br ${benefit.color} flex items-center justify-center mx-auto mb-4 group-hover:rotate-12 transition-transform`} data-id="element-11518">
                  <benefit.icon className="w-7 h-7 sm:w-8 sm:h-8 text-white" data-id="element-11519" />
                </div>
                <h3 className="text-base sm:text-lg font-bold text-[#132C53] mb-2" data-id="element-11520">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 text-sm mb-3" data-id="element-11521">
                  {benefit.description}
                </p>
                <div className="px-4 py-2 bg-gray-100 rounded-lg inline-block" data-id="element-11522">
                  <span className="text-sm font-bold text-gray-700" data-id="element-11523">
                    {benefit.stat}
                  </span>
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>

      {/* Final CTA Section - Mobile Optimized */}
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 sm:pb-20" data-id="element-11524">
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 2
      }} className="relative bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 rounded-2xl sm:rounded-3xl p-8 sm:p-12 text-center overflow-hidden shadow-2xl" data-id="element-11525">
          {/* Animated background pattern */}
          <div className="absolute inset-0 opacity-10" data-id="element-11526">
            <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
            backgroundSize: '30px 30px'
          }} data-id="element-11527" />
          </div>

          <div className="relative z-10" data-id="element-11528">
            <motion.div animate={{
            rotate: [0, 360],
            scale: [1, 1.2, 1]
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="w-16 h-16 sm:w-20 sm:h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-6" data-id="element-11529">
              <RocketIcon className="w-8 h-8 sm:w-10 sm:h-10 text-white" data-id="element-11530" />
            </motion.div>

            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-4 px-4" data-id="element-11531">
              Începe Acum și Deblochează Tot
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-white/90 mb-6 sm:mb-8 max-w-2xl mx-auto px-4" data-id="element-11532">
              Doar {3 - completedSteps}{' '}
              {3 - completedSteps === 1 ? 'pas' : 'pași'} te despart de cel mai
              avansat sistem de închiriere
            </p>

            <Link to={nextAction.path} className="inline-flex items-center justify-center space-x-2 sm:space-x-3 bg-white text-slate-900 px-6 sm:px-10 py-4 sm:py-5 rounded-xl sm:rounded-2xl font-bold text-base sm:text-lg hover:scale-105 transition-transform shadow-2xl group w-full sm:w-auto min-h-[56px]" data-id="element-11533">
              <nextAction.icon className="w-5 h-5 sm:w-6 sm:h-6 group-hover:rotate-12 transition-transform" data-id="element-11534" />
              <span data-id="element-11535">{nextAction.text}</span>
              <ArrowRightIcon className="w-5 h-5 sm:w-6 sm:h-6 group-hover:translate-x-2 transition-transform" data-id="element-11536" />
            </Link>

            <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-8 text-white/80" data-id="element-11537">
              <div className="flex items-center space-x-2" data-id="element-11538">
                <ClockIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-11539" />
                <span className="text-sm font-semibold" data-id="element-11540">Rapid</span>
              </div>
              <div className="flex items-center space-x-2" data-id="element-11541">
                <StarIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-11542" />
                <span className="text-sm font-semibold" data-id="element-11543">Acces instant</span>
              </div>
              <div className="flex items-center space-x-2" data-id="element-11544">
                <BriefcaseIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-11545" />
                <span className="text-sm font-semibold" data-id="element-11546">Câștiguri reale</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>;
}